import React, { useState, useRef, useEffect, useId } from 'react';

interface Props {
  text: string;
  /** Optional aria-label override for the trigger button. */
  ariaLabel?: string;
}

/**
 * Reusable info tooltip — shows an ⓘ icon that reveals explanatory text
 * on hover (desktop) and tap (mobile). Positions above the trigger and
 * clamps horizontally to stay within the viewport.
 */
export default function InfoTooltip({ text, ariaLabel }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();

  // Close on outside tap (mobile)
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Clamp tooltip horizontally so it doesn't overflow the viewport
  useEffect(() => {
    if (!open || !tooltipRef.current) return;
    const el = tooltipRef.current;
    const rect = el.getBoundingClientRect();
    if (rect.left < 8) {
      el.style.transform = `translateX(${Math.round(8 - rect.left)}px)`;
    } else if (rect.right > window.innerWidth - 8) {
      el.style.transform = `translateX(${Math.round(window.innerWidth - 8 - rect.right)}px)`;
    }
  }, [open]);

  return (
    <div className="relative inline-flex" ref={ref}>
      {/* 44x44 tap area around the 16x16 visual icon — uses div to avoid nested button when inside PillarCard */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(v => !v)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(v => !v); } }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="flex items-center justify-center flex-shrink-0 -m-3.5 p-3.5 cursor-pointer"
        aria-label={ariaLabel || 'More information'}
        aria-describedby={open ? tooltipId : undefined}
      >
        <span
          className="w-4 h-4 rounded-full flex items-center justify-center text-[10px] leading-none transition-colors"
          style={{ background: 'rgba(100,116,139,0.10)', color: '#94A3B8' }}
        >
          ⓘ
        </span>
      </div>
      {open && (
        <div
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          className="absolute z-50 rounded-xl px-3.5 py-2.5 text-xs leading-relaxed shadow-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-arete-forest dark:text-gray-100"
          style={{
            width: 280,
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: 6,
          }}
        >
          {text}
        </div>
      )}
    </div>
  );
}
